import React, { useState, useEffect, useMemo } from 'react';
import { reportCardService, studentService, subjectService } from '../../../services/index.js';
import { AlertCircle, RefreshCw, Save, CheckCircle2, Lock, X, Plus, Sparkles, Sliders, Edit3 } from 'lucide-react';

const STANDARD_SCHOOL_CLASSES = ['LKG', 'UKG', 'Class 1', 'Class 2', 'Class 3', 'Class 4', 'Class 5', 'Class 6', 'Class 7', 'Class 8', 'Class 9', 'Class 10'];
const COMMON_SCHOOL_SUBJECTS = ['KANNADA', 'ENGLISH', 'HINDI', 'MATHEMATICS', 'SCIENCE', 'SOCIAL SCIENCE', 'EVS', 'GENERAL KNOWLEDGE', 'COMPUTER SCIENCE'];

const cleanStr = (s) => (s || '').toString().toLowerCase().trim();
const cleanSec = (s) => cleanStr(s).replace(/^section\s*/i, '').trim();

const getStudentClass = (s) => {
  if (!s) return '';
  return s.className || s.academicLevel || s.studentClass || s.class || s.degree || s.standard || s.currentClass || '';
};

const normalizeClassName = (name) => {
  if (!name) return '';
  return name
    .toString()
    .toLowerCase()
    .replace(/^class\s*/i, '')
    .replace(/\s*standard$/i, '')
    .replace(/\s*std$/i, '')
    .replace(/(st|nd|rd|th)$/i, '')
    .replace(/[^a-z0-9]/g, '')
    .trim();
};

const matchClassNames = (classA, classB) => {
  if (!classA && !classB) return true;
  if (!classA || !classB) return false;
  const a = cleanStr(classA);
  const b = cleanStr(classB);
  if (a === b) return true;
  const normA = normalizeClassName(a);
  const normB = normalizeClassName(b);
  return normA.length > 0 && normA === normB;
};

const matchSection = (studentSec, filterSec) => {
  if (!filterSec || filterSec === 'ALL') return true;
  const normS = cleanSec(studentSec);
  const normF = cleanSec(filterSec);
  if (!normS && normF === 'all') return true;
  return normS === normF;
};

const MarksEntry = ({
  orgData,
  students: propStudents = [],
  classes = [],
  tests = [],
  refreshTests,
  activeAcademicYear,
  availableAcademicYears = [],
  examTypeConfigs = [],
  showSuccessNotification,
  showErrorNotification
}) => {
  const initialYear = activeAcademicYear || orgData?.academicYear || orgData?.activeAcademicYear || '2026-27';
  const [academicYear, setAcademicYear] = useState(initialYear);

  const availableClasses = useMemo(() => {
    return classes && classes.length > 0
      ? [...new Set(classes.map(c => c.className || c.name))].filter(Boolean)
      : STANDARD_SCHOOL_CLASSES;
  }, [classes]);

  const [selectedClass, setSelectedClass] = useState(
    (availableClasses && availableClasses.length > 0) ? availableClasses[0] : 'LKG'
  );
  const [selectedSection, setSelectedSection] = useState('ALL');
  const [selectedExamTitle, setSelectedExamTitle] = useState('');
  const [selectedSubjectName, setSelectedSubjectName] = useState('');
  const [allocatedSubjects, setAllocatedSubjects] = useState([]);
  const [masterSubjects, setMasterSubjects] = useState([]);
  const [schedulingSubject, setSchedulingSubject] = useState(false);
  const [loading, setLoading] = useState(false);
  const [savingMarks, setSavingMarks] = useState(false);
  const [isLocking, setIsLocking] = useState(false);
  const [studentRows, setStudentRows] = useState([]);
  const [allStudents, setAllStudents] = useState(propStudents || []);
  const [popupModal, setPopupModal] = useState({
    isOpen: false,
    type: 'SUCCESS', // 'SUCCESS' | 'LOCKED' | 'WARNING'
    title: '',
    message: '',
    details: null
  });

  // Quick edit maximum marks scheme modal state
  const [quickEditOpen, setQuickEditOpen] = useState(false);
  const [quickBaseMarks, setQuickBaseMarks] = useState(20);
  const [quickCompType, setQuickCompType] = useState('NONE');
  const [quickCompMax, setQuickCompMax] = useState(0);
  const [quickApplyClassSubjects, setQuickApplyClassSubjects] = useState(true);
  const [quickApplyAllClasses, setQuickApplyAllClasses] = useState(false);
  const [quickSaving, setQuickSaving] = useState(false);

  // Fetch allocated and master subjects for the selected class to populate full subject pool
  useEffect(() => {
    const fetchClassSubjectData = async () => {
      let orgId = orgData?.orgId || localStorage.getItem('orgId') || 'SCH-02';
      const normYear = normalizeAcademicYear(academicYear);
      try {
        const [allocs, masters] = await Promise.all([
          subjectService.getOrgSubjectAllocations(orgId, normYear).catch(() => []),
          subjectService.getSubjectsByOrg(orgId).catch(() => [])
        ]);
        if (Array.isArray(allocs)) {
          const filtered = allocs.filter(a => a.className && matchClassNames(a.className, selectedClass));
          setAllocatedSubjects(filtered);
        }
        if (Array.isArray(masters)) {
          setMasterSubjects(masters);
        }
      } catch (err) {
        console.error('Failed to load curriculum subjects in MarksEntry:', err);
      }
    };
    if (selectedClass) {
      fetchClassSubjectData();
    }
  }, [orgData?.orgId, academicYear, selectedClass]);

  const handleToggleLock = async (testId, newLockState) => {
    setIsLocking(true);
    try {
      const res = await reportCardService.lockTest(testId, newLockState);
      if (res && (res.success || res.isLocked !== undefined)) {
        setPopupModal({
          isOpen: true,
          type: newLockState ? 'LOCKED' : 'SUCCESS',
          title: newLockState ? 'Marks Entry Locked' : 'Marks Entry Unlocked',
          message: newLockState 
            ? 'Marks for this subject have been locked. Teachers can no longer enter or edit marks in the mobile app.'
            : 'Marks entry has been unlocked. Teachers can now enter or update marks in the mobile app.',
          details: null
        });
        if (refreshTests) {
          refreshTests();
        }
      } else {
        setPopupModal({
          isOpen: true,
          type: 'WARNING',
          title: 'Action Failed',
          message: res?.error || 'Failed to update lock status',
          details: null
        });
      }
    } catch (err) {
      setPopupModal({
        isOpen: true,
        type: 'WARNING',
        title: 'Error',
        message: err.message || 'Error updating lock status',
        details: null
      });
    } finally {
      setIsLocking(false);
    }
  };

  const handleOpenQuickEditMarks = () => {
    setQuickBaseMarks(examConfig.baseMaxMarks || 20);
    setQuickCompType(examConfig.componentType || 'NONE');
    setQuickCompMax(examConfig.componentMaxMarks || 0);
    setQuickApplyClassSubjects(true);
    setQuickApplyAllClasses(false);
    setQuickEditOpen(true);
  };

  const handleSaveQuickEditMarks = async (e) => {
    if (e) e.preventDefault();
    const bMarks = parseInt(quickBaseMarks, 10) || 0;
    const cType = quickCompType || 'NONE';
    const cMarks = cType !== 'NONE' ? (parseInt(quickCompMax, 10) || 0) : 0;
    const totalMarks = bMarks + cMarks;

    if (bMarks <= 0) {
      alert('Base maximum marks must be greater than 0.');
      return;
    }

    setQuickSaving(true);
    try {
      const testsToUpdate = new Map();
      if (activeTest && activeTest.testId) {
        testsToUpdate.set(activeTest.testId, activeTest);
      }

      if (quickApplyClassSubjects) {
        classTests.forEach(t => {
          if (t.testId && cleanStr(t.title) === cleanStr(selectedExamTitle)) {
            testsToUpdate.set(t.testId, t);
          }
        });
      }

      if (quickApplyAllClasses) {
        tests.forEach(t => {
          if (t.testId && cleanStr(t.title) === cleanStr(selectedExamTitle)) {
            testsToUpdate.set(t.testId, t);
          }
        });
      }

      let updatedCount = 0;
      for (const [tId, tObj] of testsToUpdate.entries()) {
        try {
          const payload = {
            title: tObj.title,
            baseMaxMarks: bMarks,
            componentType: cType,
            componentMaxMarks: cMarks,
            maxMarks: totalMarks
          };
          const res = await reportCardService.updateTest(tId, payload);
          if (res && (res.success || res.test || res.testId)) {
            updatedCount++;
          }
        } catch (tErr) {
          console.warn(`Failed to update test ${tId}:`, tErr);
        }
      }

      if (showSuccessNotification) {
        showSuccessNotification(
          'Marks Scheme Updated',
          `Successfully updated maximum marks to ${totalMarks} (${bMarks} base${cType !== 'NONE' ? ` + ${cMarks} ${cType}` : ''}) for ${updatedCount} subject(s).`
        );
      } else {
        alert(`Successfully updated maximum marks to ${totalMarks} for ${updatedCount} subject(s).`);
      }

      setQuickEditOpen(false);
      if (typeof refreshTests === 'function') {
        await refreshTests();
      }
    } catch (err) {
      if (showErrorNotification) {
        showErrorNotification('Update Failed', err.message);
      } else {
        alert(`Update failed: ${err.message}`);
      }
    } finally {
      setQuickSaving(false);
    }
  };

  const resolveUserRole = () => {
    let r = orgData?.role || localStorage.getItem('userRole') || localStorage.getItem('role');
    if (!r) {
      try {
        const u = JSON.parse(localStorage.getItem('user') || '{}');
        r = u.role;
      } catch (ignored) {}
    }
    return (r || 'SCHOOL_ADMIN').toUpperCase().trim();
  };

  const userRole = resolveUserRole();
  const isAdmin = ['SCHOOL_ADMIN', 'COLLEGE_ADMIN', 'SUPER_ADMIN', 'ADMIN', 'PRINCIPAL', 'HEAD_ADMIN'].includes(userRole);

  const hasExistingMarks = useMemo(() => {
    return studentRows.some(r => r.marksObtained !== '' && r.marksObtained !== null && r.marksObtained !== undefined);
  }, [studentRows]);

  // Synchronize selectedClass whenever availableClasses becomes available or updates
  useEffect(() => {
    if (availableClasses.length > 0 && !availableClasses.includes(selectedClass)) {
      setSelectedClass(availableClasses[0]);
    }
  }, [availableClasses, selectedClass]);

  // Reset exam and subject selection when class or section changes
  useEffect(() => {
    setSelectedExamTitle('');
    setSelectedSubjectName('');
  }, [selectedClass, selectedSection, academicYear]);

  const normalizeAcademicYear = (year) => {
    if (!year) return year;
    const match = year.match(/^(\d{4})-(\d{2})$/);
    if (match) {
      const century = match[1].slice(0, 2);
      return `${match[1]}-${century}${match[2]}`;
    }
    return year;
  };

  // Dynamic academic year list
  const academicYearOptions = useMemo(() => {
    if (availableAcademicYears && availableAcademicYears.length > 0) {
      return availableAcademicYears;
    }
    const years = new Set();
    if (academicYear) years.add(academicYear);
    const curYear = new Date().getFullYear();
    for (let i = -2; i <= 2; i++) {
      const y = curYear + i;
      years.add(`${y}-${(y + 1).toString().slice(-2)}`);
    }
    return Array.from(years).sort().reverse();
  }, [availableAcademicYears, academicYear]);

  useEffect(() => {
    if (activeAcademicYear) {
      setAcademicYear(activeAcademicYear);
    }
  }, [activeAcademicYear]);

  /**
   * DB-driven sections for the selected class.
   * Derived from classes prop — never shows A/B/C/D unless those sections
   * actually exist in the database for the selected class.
   */
  const sectionOptions = useMemo(() => {
    if (!classes || classes.length === 0) return [];
    const relevant = classes.filter(c => matchClassNames(c.className || c.name, selectedClass));
    const unique = [...new Set(relevant.map(c => c.section || c.sectionName).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    return unique;
  }, [classes, selectedClass]);

  // Load students self-contained if needed
  const fetchStudentsDirectly = async () => {
    try {
      const orgId = orgData?.orgId || localStorage.getItem('orgId') || 'SCH-02';
      const res = await studentService.getStudents(orgId);
      if (Array.isArray(res)) setAllStudents(res);
      else if (res && Array.isArray(res.students)) setAllStudents(res.students);
      else if (res && Array.isArray(res.data)) setAllStudents(res.data);
    } catch (err) {
      console.error('Failed to load students directly:', err);
    }
  };

  useEffect(() => {
    if (!propStudents || propStudents.length === 0) {
      fetchStudentsDirectly();
    } else {
      setAllStudents(propStudents);
    }
  }, [propStudents]);

  useEffect(() => {
    if (typeof refreshTests === 'function') {
      refreshTests();
    }
  }, []);

  // Tests for current class, section, and academic year
  const classTests = useMemo(() => {
    return tests.filter(t => {
      // Exclude teacher classroom tests — only official school exams
      if (t.createdByRole === 'TEACHER' && t.teacherId && t.teacherId !== 'ADMIN' && t.testType !== 'EXAM' && t.testType !== 'SCHOOL_EXAM') {
        return false;
      }
      // Academic year match
      const tYear = (t.description || t.academicYear || '').trim();
      if (tYear && /^\d{4}-\d{2,4}$/.test(tYear)) {
        const normT = normalizeAcademicYear(tYear);
        const normActive = normalizeAcademicYear(academicYear);
        if (normT !== normActive) return false;
      }
      const matchClass = !selectedClass || matchClassNames(t.className, selectedClass);
      const tSec = cleanSec(t.section);
      const selSec = cleanSec(selectedSection);
      const matchSec = !selectedSection || selectedSection === 'ALL' || tSec === 'all' || tSec === '' || tSec === selSec;
      return matchClass && matchSec;
    });
  }, [tests, academicYear, selectedClass, selectedSection]);

  // Distinct exam titles for this class, ordered by exam type catalog
  const availableExamTitles = useMemo(() => {
    const titles = [...new Set(classTests.map(t => t.title).filter(Boolean))];
    if (examTypeConfigs && examTypeConfigs.length > 0) {
      const orderMap = {};
      examTypeConfigs.forEach((et, idx) => { orderMap[et.examCode] = et.sortOrder || idx + 1; });
      titles.sort((a, b) => {
        const oa = orderMap[a] !== undefined ? orderMap[a] : 9999;
        const ob = orderMap[b] !== undefined ? orderMap[b] : 9999;
        return oa - ob;
      });
    }
    return titles;
  }, [classTests, examTypeConfigs]);

  // Auto-select first exam title if not selected or invalid
  useEffect(() => {
    if (availableExamTitles.length > 0) {
      if (!selectedExamTitle || !availableExamTitles.includes(selectedExamTitle)) {
        setSelectedExamTitle(availableExamTitles[0]);
      }
    } else {
      setSelectedExamTitle('');
      setSelectedSubjectName('');
    }
  }, [availableExamTitles, selectedExamTitle]);

  // Combined subject catalog: scheduled tests + full curriculum subjects for this class
  const { scheduledSubjects, unscheduledSubjects, allSubjectOptions } = useMemo(() => {
    if (!selectedExamTitle) {
      return { scheduledSubjects: [], unscheduledSubjects: [], allSubjectOptions: [] };
    }

    // 1. Subjects already scheduled as tests for this exam in this class
    const scheduled = [...new Set(
      classTests
        .filter(t => t.title === selectedExamTitle)
        .map(t => (t.subjectName || '').trim())
        .filter(Boolean)
    )];

    // 2. Pool of all curriculum subjects for this school/class
    const curriculumSet = new Set();
    allocatedSubjects.forEach(a => a.subjectName && curriculumSet.add(a.subjectName.trim().toUpperCase()));
    masterSubjects.forEach(s => s.subjectName && curriculumSet.add(s.subjectName.trim().toUpperCase()));
    COMMON_SCHOOL_SUBJECTS.forEach(s => curriculumSet.add(s.trim().toUpperCase()));

    const scheduledUpper = new Set(scheduled.map(s => s.toUpperCase()));
    const unscheduled = Array.from(curriculumSet).filter(s => !scheduledUpper.has(s.toUpperCase()));

    return {
      scheduledSubjects: scheduled,
      unscheduledSubjects: unscheduled,
      allSubjectOptions: [...scheduled, ...unscheduled]
    };
  }, [classTests, selectedExamTitle, allocatedSubjects, masterSubjects]);

  // Auto-select first subject if not selected or invalid
  useEffect(() => {
    if (allSubjectOptions.length > 0) {
      const matchFound = allSubjectOptions.some(s => s.toUpperCase() === (selectedSubjectName || '').toUpperCase());
      if (!selectedSubjectName || !matchFound) {
        setSelectedSubjectName(allSubjectOptions[0]);
      }
    } else {
      setSelectedSubjectName('');
    }
  }, [allSubjectOptions, selectedSubjectName]);

  // Find exact test object matching selectedExamTitle and selectedSubjectName
  const activeTest = useMemo(() => {
    if (!selectedExamTitle || !selectedSubjectName) return null;
    return classTests.find(
      t => t.title === selectedExamTitle && (t.subjectName || '').toUpperCase() === selectedSubjectName.toUpperCase()
    );
  }, [classTests, selectedExamTitle, selectedSubjectName]);

  const defaultMaxMarks = useMemo(() => {
    if (activeTest && activeTest.maxMarks) return activeTest.maxMarks;
    const foundPreset = examTypeConfigs?.find(et => et.examCode === selectedExamTitle);
    if (foundPreset && foundPreset.defaultMaxMarks) return foundPreset.defaultMaxMarks;
    const upper = (selectedExamTitle || '').toUpperCase();
    if (upper.includes('SA') || upper.includes('TERM') || upper.includes('ANNUAL') || upper.includes('FINAL')) return 80;
    return 40;
  }, [activeTest, examTypeConfigs, selectedExamTitle]);

  // Sibling test in this exam group to inherit setup if current subject is unscheduled
  const examGroupSibling = useMemo(() => {
    return classTests.find(t => t.title === selectedExamTitle && (t.componentType || t.baseMaxMarks));
  }, [classTests, selectedExamTitle]);

  // Exam Setup configuration is the SOURCE OF TRUTH
  const examConfig = useMemo(() => {
    const sourceTest = activeTest || examGroupSibling;
    let cType = sourceTest?.componentType || 'NONE';
    let bMax = sourceTest?.baseMaxMarks;
    let cMax = sourceTest?.componentMaxMarks !== undefined ? sourceTest.componentMaxMarks : 0;
    let tMax = sourceTest?.maxMarks;

    if (!bMax) {
      if (tMax) {
        bMax = (cType !== 'NONE' && cMax > 0 && tMax > cMax) ? (tMax - cMax) : tMax;
      } else {
        bMax = defaultMaxMarks;
      }
    }
    if (cType === 'NONE') {
      cMax = 0;
    }
    const computedTotal = (parseInt(bMax, 10) || 0) + (cType !== 'NONE' ? (parseInt(cMax, 10) || 0) : 0);
    const finalTotal = computedTotal > 0 ? computedTotal : (tMax || defaultMaxMarks);

    return {
      componentType: cType, // 'NONE' | 'ORAL' | 'INTERNAL'
      baseMaxMarks: parseInt(bMax, 10) || 40,
      componentMaxMarks: parseInt(cMax, 10) || 0,
      totalMaxMarks: finalTotal
    };
  }, [activeTest, examGroupSibling, defaultMaxMarks]);

  const hasAdditionalComponent = examConfig.componentType === 'ORAL' || examConfig.componentType === 'INTERNAL';
  const isOral = examConfig.componentType === 'ORAL';
  const isInternal = examConfig.componentType === 'INTERNAL';

  const primaryLabel = isInternal ? 'Theory' : (isOral ? 'Written' : 'Marks');
  const secondaryLabel = isInternal ? 'Internal' : (isOral ? 'Oral' : '');
  const baseMax = examConfig.baseMaxMarks;
  const compMax = examConfig.componentMaxMarks;
  const maxMarks = examConfig.totalMaxMarks;
  const totalMax = examConfig.totalMaxMarks;

  // Load results whenever activeTest or selectedSubjectName changes
  useEffect(() => {
    if (activeTest && activeTest.testId) {
      loadTestResultsAndStudents(activeTest.testId);
    } else if (selectedExamTitle && selectedSubjectName) {
      // Subject not yet scheduled as a test: load enrolled students ready for marks input!
      loadTestResultsAndStudents(null);
    } else {
      setStudentRows([]);
    }
  }, [activeTest, selectedExamTitle, selectedSubjectName, allStudents, selectedClass, selectedSection]);

  const loadTestResultsAndStudents = async (testId) => {
    setLoading(true);
    try {
      // 1. Filter students in class & section
      let classStudents = allStudents.filter(s => {
        const sClass = getStudentClass(s);
        const matchClass = matchClassNames(sClass, selectedClass);
        const matchSec = matchSection(s.section || s.sectionName, selectedSection);
        return matchClass && matchSec;
      });

      if (classStudents.length === 0 && selectedClass) {
        classStudents = allStudents.filter(s => {
          const sClass = getStudentClass(s);
          return matchClassNames(sClass, selectedClass);
        });
      }

      // Sort by Roll No / Name
      classStudents.sort((a, b) => {
        const rollA = parseInt(a.rollNo || a.rollNumber || 999999, 10);
        const rollB = parseInt(b.rollNo || b.rollNumber || 999999, 10);
        if (!isNaN(rollA) && !isNaN(rollB) && rollA !== rollB) return rollA - rollB;
        return (a.name || '').localeCompare(b.name || '');
      });

      // 2. Fetch existing results if test exists
      let existingResults = [];
      if (testId) {
        const res = await reportCardService.getTestResults(testId);
        existingResults = (res && res.success && Array.isArray(res.results)) ? res.results : [];
      }

      // 3. Build student rows with sub-component values
      const rows = classStudents.map(student => {
        const result = existingResults.find(r => {
          if (!r) return false;
          const rId = String(r.studentId || '').trim().toLowerCase();
          const sId = String(student.studentId || '').trim().toLowerCase();
          const sPk = String(student.id || '').trim().toLowerCase();
          const sRoll = String(student.rollNo || student.rollNumber || '').trim().toLowerCase();
          const sReg = String(student.regNo || student.admissionNo || '').trim().toLowerCase();
          return (
            (sId && rId === sId) ||
            (sPk && rId === sPk) ||
            (sRoll && rId === sRoll) ||
            (sReg && rId === sReg)
          );
        });
        const marks = result ? (result.marksObtained !== null && result.marksObtained !== undefined ? result.marksObtained : '') : '';
        
        let primaryVal = '';
        let secondaryVal = '';
        if (result) {
          primaryVal = (result.theoryMarks !== null && result.theoryMarks !== undefined) ? result.theoryMarks : '';
          if (isInternal) {
            secondaryVal = (result.internalMarks !== null && result.internalMarks !== undefined)
              ? result.internalMarks
              : (result.oralMarks !== null && result.oralMarks !== undefined ? result.oralMarks : '');
          } else if (isOral) {
            secondaryVal = (result.oralMarks !== null && result.oralMarks !== undefined)
              ? result.oralMarks
              : (result.internalMarks !== null && result.internalMarks !== undefined ? result.internalMarks : '');
          }
          if (primaryVal === '' && marks !== '' && !hasAdditionalComponent) {
            primaryVal = marks;
          }
        }

        return {
          studentId: student.studentId,
          name: student.name || student.studentName || 'Student',
          rollNumber: student.rollNo || student.rollNumber || student.regNo || student.admissionNo || student.studentId || '—',
          primaryMarks: primaryVal,
          secondaryMarks: secondaryVal,
          marksObtained: marks,
          attendanceStatus: result && result.attendanceStatus ? result.attendanceStatus : 'PRESENT',
          remarks: result ? result.remarks : ''
        };
      });

      setStudentRows(rows);
    } catch (err) {
      console.error('Failed to load marks matrix:', err);
      showErrorNotification('Error', 'Failed to load test results.');
    } finally {
      setLoading(false);
    }
  };

  // Schedule a single subject on-demand
  const handleQuickScheduleSubject = async () => {
    if (!selectedExamTitle || !selectedSubjectName) return;
    setSchedulingSubject(true);
    try {
      const normYear = normalizeAcademicYear(academicYear);
      const alloc = allocatedSubjects.find(a => (a.subjectName || '').toUpperCase() === selectedSubjectName.toUpperCase());

      const payload = {
        title: selectedExamTitle,
        description: normYear,
        className: selectedClass,
        section: selectedSection || 'ALL',
        semester: (selectedExamTitle.includes('SA-2') || selectedExamTitle.includes('Final')) ? 'Term 2' : 'Term 1',
        subjectName: selectedSubjectName,
        subjectId: alloc ? alloc.subjectId : -1,
        testDate: new Date().toISOString().slice(0, 10),
        baseMaxMarks: baseMax,
        componentType: examConfig.componentType,
        componentMaxMarks: compMax,
        maxMarks: totalMax,
        testType: 'EXAM',
        instructions: 'Official School Examination',
        teacherId: (alloc && alloc.teacherId) ? String(alloc.teacherId) : 'ADMIN',
        adminEmail: orgData?.email || 'admin@school.edu'
      };

      const res = await reportCardService.createTest(payload);
      if (res && res.success) {
        showSuccessNotification('Subject Scheduled', `Successfully scheduled "${selectedSubjectName}" for ${selectedExamTitle} (${selectedClass}).`);
        if (typeof refreshTests === 'function') refreshTests();
      } else {
        showErrorNotification('Failed', res?.error || 'Failed to schedule subject.');
      }
    } catch (err) {
      showErrorNotification('Error', err.message);
    } finally {
      setSchedulingSubject(false);
    }
  };

  // Schedule all unscheduled subjects at once
  const handleScheduleAllMissingSubjects = async () => {
    if (unscheduledSubjects.length === 0) {
      alert('All curriculum subjects are already scheduled for this exam.');
      return;
    }
    if (!window.confirm(`Schedule all ${unscheduledSubjects.length} missing subject(s) (${unscheduledSubjects.join(', ')}) for ${selectedExamTitle} in ${selectedClass}?`)) {
      return;
    }
    setSchedulingSubject(true);
    try {
      const normYear = normalizeAcademicYear(academicYear);
      let count = 0;

      for (const subj of unscheduledSubjects) {
        const alloc = allocatedSubjects.find(a => (a.subjectName || '').toUpperCase() === subj.toUpperCase());
        const payload = {
          title: selectedExamTitle,
          description: normYear,
          className: selectedClass,
          section: selectedSection || 'ALL',
          semester: (selectedExamTitle.includes('SA-2') || selectedExamTitle.includes('Final')) ? 'Term 2' : 'Term 1',
          subjectName: subj,
          subjectId: alloc ? alloc.subjectId : -1,
          testDate: new Date().toISOString().slice(0, 10),
          baseMaxMarks: baseMax,
          componentType: examConfig.componentType,
          componentMaxMarks: compMax,
          maxMarks: totalMax,
          testType: 'EXAM',
          instructions: 'Official School Examination',
          teacherId: (alloc && alloc.teacherId) ? String(alloc.teacherId) : 'ADMIN',
          adminEmail: orgData?.email || 'admin@school.edu'
        };
        const res = await reportCardService.createTest(payload);
        if (res && res.success) count++;
      }

      showSuccessNotification('Subjects Scheduled', `Successfully scheduled ${count} subject(s) for ${selectedExamTitle} (${selectedClass}).`);
      if (typeof refreshTests === 'function') refreshTests();
    } catch (err) {
      showErrorNotification('Error', err.message);
    } finally {
      setSchedulingSubject(false);
    }
  };

  const handleComponentChange = (idx, field, rawValue) => {
    const next = [...studentRows];
    const row = { ...next[idx] };
    row[field] = rawValue;

    const p = parseFloat(field === 'primaryMarks' ? rawValue : row.primaryMarks);
    const s = parseFloat(field === 'secondaryMarks' ? rawValue : row.secondaryMarks);

    const hasP = !isNaN(p) && (field === 'primaryMarks' ? rawValue !== '' : (row.primaryMarks !== '' && row.primaryMarks !== null && row.primaryMarks !== undefined));
    const hasS = !isNaN(s) && (field === 'secondaryMarks' ? rawValue !== '' : (row.secondaryMarks !== '' && row.secondaryMarks !== null && row.secondaryMarks !== undefined));

    if (hasP || hasS) {
      row.marksObtained = (hasP ? p : 0) + (hasS ? s : 0);
    } else {
      row.marksObtained = '';
    }
    next[idx] = row;
    setStudentRows(next);
  };

  const handleRowChange = (idx, field, value) => {
    const next = [...studentRows];
    next[idx][field] = value;
    if (field === 'attendanceStatus' && (value === 'ABSENT' || value === 'WITHHELD' || value === 'EXEMPTED')) {
      next[idx].marksObtained = 0;
      next[idx].primaryMarks = 0;
      next[idx].secondaryMarks = 0;
    }
    setStudentRows(next);
  };

  const handleMarkAllPresent = () => {
    const next = studentRows.map(r => ({ ...r, attendanceStatus: 'PRESENT' }));
    setStudentRows(next);
  };

  const handleMarkAllAbsent = () => {
    const next = studentRows.map(r => ({ ...r, attendanceStatus: 'ABSENT', marksObtained: 0, primaryMarks: 0, secondaryMarks: 0 }));
    setStudentRows(next);
  };

  const handleClearMarks = () => {
    if (!window.confirm('Clear all entered marks on this screen?')) return;
    const next = studentRows.map(r => ({ ...r, marksObtained: '', primaryMarks: '', secondaryMarks: '' }));
    setStudentRows(next);
  };

  const calculateGrade = (obtained, max) => {
    if (obtained === '' || obtained === null || obtained === undefined || isNaN(obtained) || max <= 0) return '—';
    const pct = (parseFloat(obtained) / parseFloat(max)) * 100;
    if (pct >= 90) return 'A+';
    if (pct >= 80) return 'A';
    if (pct >= 70) return 'B';
    if (pct >= 60) return 'C';
    if (pct >= 50) return 'D';
    if (pct >= 35) return 'E';
    return 'F';
  };

  const calculatePercentage = (obtained, max) => {
    if (obtained === '' || obtained === null || obtained === undefined || isNaN(obtained) || max <= 0) return '—';
    return `${((parseFloat(obtained) / parseFloat(max)) * 100).toFixed(0)}%`;
  };

  const handleSaveMarks = async () => {
    if (!selectedExamTitle || !selectedSubjectName) {
      alert('Please select an exam and subject first.');
      return;
    }

    for (let r of studentRows) {
      if (r.attendanceStatus === 'PRESENT') {
        if (hasAdditionalComponent) {
          if (r.primaryMarks !== '' && r.primaryMarks !== null && !isNaN(r.primaryMarks)) {
            const pVal = parseFloat(r.primaryMarks);
            if (pVal < 0 || pVal > baseMax) {
              alert(`${primaryLabel} marks for ${r.name} cannot exceed ${baseMax}.`);
              return;
            }
          }
          if (r.secondaryMarks !== '' && r.secondaryMarks !== null && !isNaN(r.secondaryMarks)) {
            const sVal = parseFloat(r.secondaryMarks);
            if (sVal < 0 || sVal > compMax) {
              alert(`${secondaryLabel} marks for ${r.name} cannot exceed ${compMax}.`);
              return;
            }
          }
        }
        if (r.marksObtained === '' || r.marksObtained === undefined || r.marksObtained === null) {
          alert(`Please enter marks for ${r.name} (or mark as Absent/Exempted).`);
          return;
        }
        const val = parseFloat(r.marksObtained);
        if (isNaN(val) || val < 0 || val > maxMarks) {
          alert(`Total marks for ${r.name} must be between 0 and ${maxMarks}.`);
          return;
        }
      }
    }

    setSavingMarks(true);
    try {
      let currentTestId = activeTest?.testId;

      // Auto-create test record on-the-fly if not yet scheduled!
      if (!currentTestId) {
        const normYear = normalizeAcademicYear(academicYear);
        const alloc = allocatedSubjects.find(a => (a.subjectName || '').toUpperCase() === selectedSubjectName.toUpperCase());

        const payload = {
          title: selectedExamTitle,
          description: normYear,
          className: selectedClass,
          section: selectedSection || 'ALL',
          semester: (selectedExamTitle.includes('SA-2') || selectedExamTitle.includes('Final')) ? 'Term 2' : 'Term 1',
          subjectName: selectedSubjectName,
          subjectId: alloc ? alloc.subjectId : -1,
          testDate: new Date().toISOString().slice(0, 10),
          baseMaxMarks: baseMax,
          componentType: examConfig.componentType,
          componentMaxMarks: compMax,
          maxMarks: maxMarks,
          testType: 'EXAM',
          instructions: 'Official School Examination',
          teacherId: (alloc && alloc.teacherId) ? String(alloc.teacherId) : 'ADMIN',
          adminEmail: orgData?.email || 'admin@school.edu'
        };

        const createRes = await reportCardService.createTest(payload);
        if (createRes && createRes.success && createRes.testId) {
          currentTestId = createRes.testId;
        } else {
          throw new Error(createRes?.error || 'Failed to auto-schedule exam test for this subject.');
        }
      }

      const resultsData = studentRows.map(r => {
        const isPresent = r.attendanceStatus === 'PRESENT';
        const total = isPresent ? (r.marksObtained !== '' && r.marksObtained !== null && !isNaN(r.marksObtained) ? Math.round(parseFloat(r.marksObtained)) : 0) : 0;
        const primaryNum = (isPresent && r.primaryMarks !== '' && r.primaryMarks !== null && !isNaN(r.primaryMarks)) ? Math.round(parseFloat(r.primaryMarks)) : null;
        const secondaryNum = (isPresent && r.secondaryMarks !== '' && r.secondaryMarks !== null && !isNaN(r.secondaryMarks)) ? Math.round(parseFloat(r.secondaryMarks)) : null;

        return {
          studentId: r.studentId,
          marksObtained: total,
          theoryMarks: hasAdditionalComponent ? primaryNum : null,
          oralMarks: isOral ? secondaryNum : null,
          internalMarks: isInternal ? secondaryNum : null,
          attendanceStatus: r.attendanceStatus,
          remarks: r.remarks || ''
        };
      });

      const res = await reportCardService.uploadTestResults(currentTestId, resultsData);
      if (res && res.success) {
        const msg = res.message || `Recorded marks for ${resultsData.length} students in ${selectedExamTitle} (${selectedSubjectName}).`;
        setPopupModal({
          isOpen: true,
          type: 'SUCCESS',
          title: 'Marks Saved Successfully',
          message: `${msg} These marks are securely stored and report cards can now be compiled.`
        });
        if (typeof refreshTests === 'function') refreshTests();
        loadTestResultsAndStudents(currentTestId);
      } else {
        let errStr = res.error || 'Failed to save marks.';
        const isLocked = res.isLocked || errStr.toLowerCase().includes('locked') || errStr.toLowerCase().includes('frozen') || errStr.toLowerCase().includes('only');
        setPopupModal({
          isOpen: true,
          type: isLocked ? 'LOCKED' : 'WARNING',
          title: isLocked ? 'Marks Locked & Protected' : 'Notice',
          message: errStr
        });
      }
    } catch (err) {
      let raw = err.message || '';
      try {
        if (raw.includes('{')) {
          const parsed = JSON.parse(raw.slice(raw.indexOf('{')));
          if (parsed.error) raw = parsed.error;
        }
      } catch (ignored) {}
      raw = raw.replace(/^HTTP \d+:\s*/i, '');
      const isLocked = raw.toLowerCase().includes('locked') || raw.toLowerCase().includes('frozen') || raw.toLowerCase().includes('only');
      setPopupModal({
        isOpen: true,
        type: isLocked ? 'LOCKED' : 'WARNING',
        title: isLocked ? 'Marks Locked & Protected' : 'Notice',
        message: raw || 'Failed to save marks.'
      });
    } finally {
      setSavingMarks(false);
    }
  };

  return (
    <div style={{ background: '#ffffff', borderRadius: '12px', padding: '22px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.03)' }}>
      
      {/* Title */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: '#0f172a', letterSpacing: '-0.3px' }}>
            Marks Entry
          </h3>
          <p style={{ margin: '3px 0 0 0', fontSize: '13px', color: '#64748b' }}>
            Enter and verify student examination marks.
          </p>
        </div>

        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            type="button"
            onClick={() => { fetchStudentsDirectly(); if (activeTest) loadTestResultsAndStudents(activeTest.testId); }}
            style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', background: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: '7px', fontSize: '12.5px', fontWeight: '700', color: '#334155', cursor: 'pointer' }}
          >
            <RefreshCw size={13} /> Refresh
          </button>
        </div>
      </div>

      {/* Clean 5-Dropdown Selector Bar */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
        gap: '12px',
        background: '#f8fafc',
        padding: '16px',
        borderRadius: '10px',
        border: '1px solid #e2e8f0',
        marginBottom: '20px'
      }}>
        {/* Academic Year */}
        <div>
          <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#475569', marginBottom: '4px', textTransform: 'uppercase' }}>
            Academic Year
          </label>
          <select
            value={academicYear}
            onChange={(e) => setAcademicYear(e.target.value)}
            style={{ width: '100%', padding: '9px 12px', borderRadius: '7px', border: '1.5px solid #cbd5e1', fontSize: '13px', fontWeight: '700', background: '#ffffff' }}
          >
            {academicYearOptions.map(yr => (
              <option key={yr} value={yr}>{yr} Session</option>
            ))}
          </select>
        </div>

        {/* Class */}
        <div>
          <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#475569', marginBottom: '4px', textTransform: 'uppercase' }}>
            Class
          </label>
          <select
            value={selectedClass}
            onChange={(e) => setSelectedClass(e.target.value)}
            style={{ width: '100%', padding: '9px 12px', borderRadius: '7px', border: '1.5px solid #cbd5e1', fontSize: '13px', fontWeight: '700', background: '#ffffff' }}
          >
            {availableClasses.map(cls => (
              <option key={cls} value={cls}>{cls}</option>
            ))}
          </select>
        </div>

        {/* Section */}
        <div>
          <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#475569', marginBottom: '4px', textTransform: 'uppercase' }}>
            Section
          </label>
          <select
            value={selectedSection}
            onChange={(e) => setSelectedSection(e.target.value)}
            style={{ width: '100%', padding: '9px 12px', borderRadius: '7px', border: '1.5px solid #cbd5e1', fontSize: '13px', fontWeight: '700', background: '#ffffff' }}
          >
          <option value="ALL">All Sections</option>
            {sectionOptions.length > 0 ? (
              sectionOptions.map(sec => (
                <option key={sec} value={sec}>Section {sec}</option>
              ))
            ) : (
              <option value="" disabled>No sections in DB for this class</option>
            )}
          </select>
        </div>

        {/* Exam Title */}
        <div>
          <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#475569', marginBottom: '4px', textTransform: 'uppercase' }}>
            Exam
          </label>
          <select
            value={selectedExamTitle}
            onChange={(e) => setSelectedExamTitle(e.target.value)}
            disabled={availableExamTitles.length === 0}
            style={{ width: '100%', padding: '9px 12px', borderRadius: '7px', border: '1.5px solid #cbd5e1', fontSize: '13px', fontWeight: '700', background: '#ffffff' }}
          >
            {availableExamTitles.length === 0 ? (
              <option value="">No exams scheduled</option>
            ) : (
              availableExamTitles.map(ex => (
                <option key={ex} value={ex}>{ex}</option>
              ))
            )}
          </select>
        </div>

        {/* Subject */}
        <div>
          <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#475569', marginBottom: '4px', textTransform: 'uppercase' }}>
            Subject
          </label>
          <select
            value={selectedSubjectName}
            onChange={(e) => setSelectedSubjectName(e.target.value)}
            disabled={allSubjectOptions.length === 0 || !selectedExamTitle}
            style={{ width: '100%', padding: '9px 12px', borderRadius: '7px', border: '1.5px solid #cbd5e1', fontSize: '13px', fontWeight: '700', background: '#ffffff' }}
          >
            {allSubjectOptions.length === 0 ? (
              <option value="">No subjects found</option>
            ) : (
              <>
                {scheduledSubjects.length > 0 && (
                  <optgroup label="✓ Scheduled Exam Subjects">
                    {scheduledSubjects.map(sub => (
                      <option key={sub} value={sub}>{sub}</option>
                    ))}
                  </optgroup>
                )}
                {unscheduledSubjects.length > 0 && (
                  <optgroup label="➕ Other Subjects (Click to Schedule & Enter Marks)">
                    {unscheduledSubjects.map(sub => (
                      <option key={sub} value={sub}>+ {sub} (Not scheduled)</option>
                    ))}
                  </optgroup>
                )}
              </>
            )}
          </select>
        </div>
      </div>

      {/* Context Banner & Quick Fill Controls */}
      {selectedSubjectName && (
        activeTest ? (
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            background: '#eff6ff',
            border: '1px solid #bfdbfe',
            borderRadius: '8px',
            padding: '12px 18px',
            marginBottom: '16px',
            flexWrap: 'wrap',
            gap: '12px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
              <strong style={{ fontSize: '14px', color: '#1e3a8a' }}>
                {activeTest.title} • {activeTest.subjectName}
              </strong>
              <span style={{ fontSize: '12px', color: '#3b82f6' }}>
                Date: {activeTest.testDate || 'N/A'}
              </span>
              {isOral ? (
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#ffffff', padding: '3px 10px', borderRadius: '6px', border: '1.5px solid #86efac', fontSize: '12px' }}>
                  <span style={{ fontWeight: '800', color: '#166534' }}>
                    Written: {baseMax} + Oral: +{compMax} = Total: {maxMarks}
                  </span>
                  <span style={{ fontSize: '11px', color: '#15803d', background: '#dcfce7', padding: '1px 6px', borderRadius: '4px', fontWeight: '700' }}>
                    Oral is Additional (+{compMax})
                  </span>
                </div>
              ) : isInternal ? (
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#ffffff', padding: '3px 10px', borderRadius: '6px', border: '1.5px solid #93c5fd', fontSize: '12px' }}>
                  <span style={{ fontWeight: '800', color: '#1e40af' }}>
                    Theory: {baseMax} + Internal: +{compMax} = Total: {maxMarks}
                  </span>
                  <span style={{ fontSize: '11px', color: '#1d4ed8', background: '#dbeafe', padding: '1px 6px', borderRadius: '4px', fontWeight: '700' }}>
                    Internal is Additional (+{compMax})
                  </span>
                </div>
              ) : (
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#ffffff', padding: '3px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '12px' }}>
                  <span style={{ fontWeight: '700', color: '#334155' }}>
                    Total Marks: {maxMarks}
                  </span>
                </div>
              )}
              <span style={{
                padding: '2px 8px',
                borderRadius: '12px',
                fontSize: '11px',
                fontWeight: '700',
                background: activeTest.status === 'FROZEN' ? '#fee2e2' : '#dcfce7',
                color: activeTest.status === 'FROZEN' ? '#b91c1c' : '#15803d',
                border: `1px solid ${activeTest.status === 'FROZEN' ? '#fca5a5' : '#86efac'}`
              }}>
                {activeTest.status === 'FROZEN' ? '🔒 Locked (Teachers Cannot Edit)' : '● Open for Teacher Entry'}
              </span>
            </div>

            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
              {isAdmin && (
                <button
                  type="button"
                  onClick={handleOpenQuickEditMarks}
                  style={{
                    background: '#eff6ff',
                    border: '1.5px solid #bfdbfe',
                    borderRadius: '6px',
                    padding: '5px 12px',
                    fontSize: '11.5px',
                    fontWeight: '700',
                    color: '#1d4ed8',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                  }}
                  title="Configure max marks and components (Oral/IA) for this exam"
                >
                  <Sliders size={13} />
                  ⚙️ Edit Max Marks
                </button>
              )}
              {isAdmin && (
                <button
                  type="button"
                  disabled={isLocking}
                  onClick={() => handleToggleLock(activeTest.testId, activeTest.status !== 'FROZEN')}
                  style={{
                    background: activeTest.status === 'FROZEN' ? '#fef2f2' : '#ffffff',
                    border: `1px solid ${activeTest.status === 'FROZEN' ? '#f87171' : '#cbd5e1'}`,
                    borderRadius: '6px',
                    padding: '5px 12px',
                    fontSize: '11.5px',
                    fontWeight: '700',
                    color: activeTest.status === 'FROZEN' ? '#dc2626' : '#1e3a8a',
                    cursor: isLocking ? 'wait' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                  }}
                  title={activeTest.status === 'FROZEN' ? 'Unlock to allow teachers to enter marks' : 'Lock to prevent teachers from modifying marks'}
                >
                  <Lock size={13} />
                  {activeTest.status === 'FROZEN' ? '🔓 Unlock for Teachers' : '🔒 Lock Marks Entry'}
                </button>
              )}
              <button
                type="button"
                disabled={!isAdmin && hasExistingMarks}
                onClick={handleMarkAllPresent}
                style={{ background: '#ffffff', border: '1px solid #cbd5e1', borderRadius: '6px', padding: '5px 10px', fontSize: '11.5px', fontWeight: '700', color: '#16a34a', cursor: (!isAdmin && hasExistingMarks) ? 'not-allowed' : 'pointer', opacity: (!isAdmin && hasExistingMarks) ? 0.5 : 1 }}
              >
                ✓ All Present
              </button>
              <button
                type="button"
                disabled={!isAdmin && hasExistingMarks}
                onClick={handleMarkAllAbsent}
                style={{ background: '#ffffff', border: '1px solid #cbd5e1', borderRadius: '6px', padding: '5px 10px', fontSize: '11.5px', fontWeight: '700', color: '#dc2626', cursor: (!isAdmin && hasExistingMarks) ? 'not-allowed' : 'pointer', opacity: (!isAdmin && hasExistingMarks) ? 0.5 : 1 }}
              >
                ✗ All Absent
              </button>
              <button
                type="button"
                disabled={!isAdmin && hasExistingMarks}
                onClick={handleClearMarks}
                style={{ background: '#ffffff', border: '1px solid #cbd5e1', borderRadius: '6px', padding: '5px 10px', fontSize: '11.5px', fontWeight: '700', color: '#64748b', cursor: (!isAdmin && hasExistingMarks) ? 'not-allowed' : 'pointer', opacity: (!isAdmin && hasExistingMarks) ? 0.5 : 1 }}
              >
                Clear
              </button>
            </div>
          </div>
        ) : (
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            background: '#fffbeb',
            border: '1.5px solid #fde68a',
            borderRadius: '8px',
            padding: '12px 18px',
            marginBottom: '16px',
            flexWrap: 'wrap',
            gap: '12px'
          }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                <strong style={{ fontSize: '14px', color: '#92400e' }}>
                  {selectedExamTitle} • {selectedSubjectName}
                </strong>
                <span style={{
                  padding: '2px 8px',
                  borderRadius: '12px',
                  fontSize: '11px',
                  fontWeight: '700',
                  background: '#fef3c7',
                  color: '#b45309',
                  border: '1px solid #fcd34d'
                }}>
                  ⚠️ Not Yet Scheduled
                </span>
                {isOral ? (
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#ffffff', padding: '3px 10px', borderRadius: '6px', border: '1.5px solid #86efac', fontSize: '12px' }}>
                    <span style={{ fontWeight: '800', color: '#166534' }}>
                      Written: {baseMax} + Oral: +{compMax} = Total: {maxMarks}
                    </span>
                    <span style={{ fontSize: '11px', color: '#15803d', background: '#dcfce7', padding: '1px 6px', borderRadius: '4px', fontWeight: '700' }}>
                      Oral is Additional (+{compMax})
                    </span>
                  </div>
                ) : isInternal ? (
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#ffffff', padding: '3px 10px', borderRadius: '6px', border: '1.5px solid #93c5fd', fontSize: '12px' }}>
                    <span style={{ fontWeight: '800', color: '#1e40af' }}>
                      Theory: {baseMax} + Internal: +{compMax} = Total: {maxMarks}
                    </span>
                    <span style={{ fontSize: '11px', color: '#1d4ed8', background: '#dbeafe', padding: '1px 6px', borderRadius: '4px', fontWeight: '700' }}>
                      Internal is Additional (+{compMax})
                    </span>
                  </div>
                ) : (
                  <span style={{ fontSize: '12px', color: '#78350f', fontWeight: '700' }}>
                    Total Max Marks: {maxMarks}
                  </span>
                )}
              </div>
              <p style={{ margin: '4px 0 0 0', fontSize: '12.5px', color: '#92400e' }}>
                You can enter marks below and click <strong>"Save Marks"</strong> to auto-schedule, or schedule it directly:
              </p>
            </div>

            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
              {isAdmin && (
                <button
                  type="button"
                  onClick={handleOpenQuickEditMarks}
                  style={{
                    background: '#eff6ff',
                    border: '1.5px solid #bfdbfe',
                    borderRadius: '6px',
                    padding: '6px 12px',
                    fontSize: '12px',
                    fontWeight: '700',
                    color: '#1d4ed8',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                  }}
                  title="Configure max marks and components (Oral/IA) for this exam"
                >
                  <Sliders size={13} />
                  ⚙️ Edit Max Marks
                </button>
              )}
              <button
                type="button"
                disabled={schedulingSubject}
                onClick={handleQuickScheduleSubject}
                style={{
                  background: '#2563eb',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '6px 14px',
                  fontSize: '12px',
                  fontWeight: '700',
                  cursor: schedulingSubject ? 'wait' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px'
                }}
              >
                <Plus size={13} />
                {schedulingSubject ? 'Scheduling...' : `Schedule ${selectedSubjectName} Now`}
              </button>
              {unscheduledSubjects.length > 1 && (
                <button
                  type="button"
                  disabled={schedulingSubject}
                  onClick={handleScheduleAllMissingSubjects}
                  style={{
                    background: '#ffffff',
                    color: '#1e3a8a',
                    border: '1px solid #cbd5e1',
                    borderRadius: '6px',
                    padding: '6px 12px',
                    fontSize: '12px',
                    fontWeight: '700',
                    cursor: schedulingSubject ? 'wait' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px'
                  }}
                  title="Schedule all unscheduled subjects at once"
                >
                  <Sparkles size={13} /> Schedule All Missing ({unscheduledSubjects.length})
                </button>
              )}
              <button
                type="button"
                onClick={handleMarkAllPresent}
                style={{ background: '#ffffff', border: '1px solid #cbd5e1', borderRadius: '6px', padding: '5px 10px', fontSize: '11.5px', fontWeight: '700', color: '#16a34a', cursor: 'pointer' }}
              >
                ✓ All Present
              </button>
              <button
                type="button"
                onClick={handleMarkAllAbsent}
                style={{ background: '#ffffff', border: '1px solid #cbd5e1', borderRadius: '6px', padding: '5px 10px', fontSize: '11.5px', fontWeight: '700', color: '#dc2626', cursor: 'pointer' }}
              >
                ✗ All Absent
              </button>
              <button
                type="button"
                onClick={handleClearMarks}
                style={{ background: '#ffffff', border: '1px solid #cbd5e1', borderRadius: '6px', padding: '5px 10px', fontSize: '11.5px', fontWeight: '700', color: '#64748b', cursor: 'pointer' }}
              >
                Clear
              </button>
            </div>
          </div>
        )
      )}

      {/* Students Marks Table */}
      {!selectedExamTitle || !selectedSubjectName ? (
        <div style={{ textAlign: 'center', padding: '48px 24px', color: '#64748b', border: '1px dashed #cbd5e1', borderRadius: '10px' }}>
          <AlertCircle size={32} color="#94a3b8" style={{ marginBottom: '8px' }} />
          <h4 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#334155' }}>
            No exam or subject selected
          </h4>
          <p style={{ margin: '4px 0 0 0', fontSize: '13px' }}>
            Select an exam and subject from the dropdown above to enter marks.
          </p>
        </div>
      ) : loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#64748b', fontSize: '14px' }}>
          Loading students and marks...
        </div>
      ) : studentRows.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#64748b', border: '1px dashed #cbd5e1', borderRadius: '10px' }}>
          No students enrolled in {selectedClass} {selectedSection !== 'ALL' ? `Section ${selectedSection}` : ''}.
        </div>
      ) : (
        <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#475569', textAlign: 'left' }}>
                <th style={{ padding: '10px 14px', width: '50px', fontWeight: '700' }}>#</th>
                <th style={{ padding: '10px 14px', fontWeight: '700' }}>Student Name</th>
                <th style={{ padding: '10px 14px', width: '110px', fontWeight: '700' }}>Roll No</th>
                <th style={{ padding: '10px 14px', width: '100px', textAlign: 'center', fontWeight: '700', color: '#475569' }}>
                  Max Marks
                </th>
                {isOral ? (
                  <>
                    <th style={{ padding: '10px 14px', width: '125px', textAlign: 'center', fontWeight: '700', color: '#1e40af' }}>
                      Written ({baseMax})
                    </th>
                    <th style={{ padding: '10px 14px', width: '125px', textAlign: 'center', fontWeight: '700', color: '#047857' }}>
                      Oral ({compMax})
                    </th>
                    <th style={{ padding: '10px 14px', width: '135px', textAlign: 'center', fontWeight: '800', color: '#0f172a' }}>
                      Obtained Marks
                    </th>
                  </>
                ) : isInternal ? (
                  <>
                    <th style={{ padding: '10px 14px', width: '125px', textAlign: 'center', fontWeight: '700', color: '#1e40af' }}>
                      Theory ({baseMax})
                    </th>
                    <th style={{ padding: '10px 14px', width: '125px', textAlign: 'center', fontWeight: '700', color: '#4338ca' }}>
                      Internal ({compMax})
                    </th>
                    <th style={{ padding: '10px 14px', width: '135px', textAlign: 'center', fontWeight: '800', color: '#0f172a' }}>
                      Obtained Marks
                    </th>
                  </>
                ) : (
                  <th style={{ padding: '10px 14px', width: '145px', textAlign: 'center', fontWeight: '800', color: '#0f172a' }}>
                    Obtained Marks
                  </th>
                )}
                <th style={{ padding: '10px 14px', width: '75px', textAlign: 'center', fontWeight: '700' }}>%</th>
                <th style={{ padding: '10px 14px', width: '75px', textAlign: 'center', fontWeight: '700' }}>Grade</th>
                <th style={{ padding: '10px 14px', width: '130px', textAlign: 'center', fontWeight: '700' }}>Attendance</th>
              </tr>
            </thead>
            <tbody>
              {studentRows.map((row, idx) => {
                const grade = calculateGrade(row.marksObtained, maxMarks);
                const pct = calculatePercentage(row.marksObtained, maxMarks);
                const isAbsent = row.attendanceStatus === 'ABSENT';

                return (
                  <tr
                    key={row.studentId || idx}
                    style={{
                      borderBottom: '1px solid #f1f5f9',
                      background: isAbsent ? '#fef2f2' : idx % 2 === 0 ? '#ffffff' : '#fafafa'
                    }}
                  >
                    <td style={{ padding: '10px 14px', color: '#64748b' }}>{idx + 1}</td>
                    <td style={{ padding: '10px 14px' }}>
                      <strong style={{ color: '#0f172a' }}>{row.name}</strong>
                      <div style={{ fontSize: '11px', color: '#94a3b8' }}>ID: {row.studentId}</div>
                    </td>
                    <td style={{ padding: '10px 14px', color: '#475569', fontWeight: '600' }}>
                      {row.rollNumber}
                    </td>
                    <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                      <span style={{
                        display: 'inline-block',
                        padding: '4px 10px',
                        borderRadius: '6px',
                        background: '#f1f5f9',
                        color: '#334155',
                        fontWeight: '700',
                        fontSize: '13px',
                        border: '1px solid #e2e8f0'
                      }}>
                        {maxMarks}
                      </span>
                    </td>

                    {hasAdditionalComponent ? (
                      <>
                        {/* Primary Mark: Written (Oral configured) or Theory (Internal configured) */}
                        <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                          <input
                            type="number"
                            min={0}
                            max={baseMax}
                            value={row.primaryMarks ?? ''}
                            disabled={isAbsent || (!isAdmin && hasExistingMarks)}
                            onChange={(e) => handleComponentChange(idx, 'primaryMarks', e.target.value)}
                            placeholder="—"
                            style={{
                              width: '75px',
                              padding: '7px 8px',
                              borderRadius: '6px',
                              border: isAbsent ? '1px solid #cbd5e1' : (!isAdmin && hasExistingMarks) ? '1px solid #e2e8f0' : '1.5px solid #3b82f6',
                              textAlign: 'center',
                              fontSize: '13.5px',
                              fontWeight: '700',
                              color: (isAbsent || (!isAdmin && hasExistingMarks)) ? '#64748b' : '#1e3a8a',
                              background: (isAbsent || (!isAdmin && hasExistingMarks)) ? '#f1f5f9' : '#ffffff',
                              cursor: (!isAdmin && hasExistingMarks) ? 'not-allowed' : 'auto'
                            }}
                            title={`${primaryLabel} (Max ${baseMax})`}
                          />
                        </td>

                        {/* Secondary Mark: Oral or Internal */}
                        <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                          <input
                            type="number"
                            min={0}
                            max={compMax}
                            value={row.secondaryMarks ?? ''}
                            disabled={isAbsent || (!isAdmin && hasExistingMarks)}
                            onChange={(e) => handleComponentChange(idx, 'secondaryMarks', e.target.value)}
                            placeholder="—"
                            style={{
                              width: '75px',
                              padding: '7px 8px',
                              borderRadius: '6px',
                              border: isAbsent ? '1px solid #cbd5e1' : (!isAdmin && hasExistingMarks) ? '1px solid #e2e8f0' : isOral ? '1.5px solid #10b981' : '1.5px solid #6366f1',
                              textAlign: 'center',
                              fontSize: '13.5px',
                              fontWeight: '700',
                              color: (isAbsent || (!isAdmin && hasExistingMarks)) ? '#64748b' : isOral ? '#065f46' : '#4338ca',
                              background: (isAbsent || (!isAdmin && hasExistingMarks)) ? '#f1f5f9' : '#ffffff',
                              cursor: (!isAdmin && hasExistingMarks) ? 'not-allowed' : 'auto'
                            }}
                            title={`${secondaryLabel} (Max ${compMax})`}
                          />
                        </td>

                        {/* Total Mark: Auto-calculated sum */}
                        <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                          <input
                            type="number"
                            min={0}
                            max={maxMarks}
                            value={row.marksObtained ?? ''}
                            disabled={isAbsent || (!isAdmin && hasExistingMarks)}
                            onChange={(e) => handleRowChange(idx, 'marksObtained', e.target.value)}
                            placeholder="—"
                            style={{
                              width: '75px',
                              padding: '7px 8px',
                              borderRadius: '6px',
                              border: isAbsent ? '1px solid #cbd5e1' : (!isAdmin && hasExistingMarks) ? '1px solid #e2e8f0' : '1.5px solid #0284c7',
                              textAlign: 'center',
                              fontSize: '13.5px',
                              fontWeight: '800',
                              color: (isAbsent || (!isAdmin && hasExistingMarks)) ? '#64748b' : '#0369a1',
                              background: (isAbsent || (!isAdmin && hasExistingMarks)) ? '#f1f5f9' : '#f0f9ff',
                              cursor: (!isAdmin && hasExistingMarks) ? 'not-allowed' : 'auto'
                            }}
                            title={`Auto-calculated total (${primaryLabel} + ${secondaryLabel})`}
                          />
                        </td>
                      </>
                    ) : (
                      <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                        <input
                          type="number"
                          min={0}
                          max={maxMarks}
                          value={row.marksObtained}
                          disabled={isAbsent || (!isAdmin && hasExistingMarks)}
                          onChange={(e) => handleRowChange(idx, 'marksObtained', e.target.value)}
                          placeholder="—"
                          style={{
                            width: '85px',
                            padding: '7px 10px',
                            borderRadius: '6px',
                            border: isAbsent ? '1px solid #cbd5e1' : (!isAdmin && hasExistingMarks) ? '1px solid #e2e8f0' : '1.5px solid #2563eb',
                            textAlign: 'center',
                            fontSize: '13.5px',
                            fontWeight: '800',
                            color: (isAbsent || (!isAdmin && hasExistingMarks)) ? '#64748b' : '#0f172a',
                            background: (isAbsent || (!isAdmin && hasExistingMarks)) ? '#f1f5f9' : '#ffffff',
                            cursor: (!isAdmin && hasExistingMarks) ? 'not-allowed' : 'auto'
                          }}
                          title={`Obtained Marks (Max ${maxMarks})`}
                        />
                      </td>
                    )}
                    <td style={{ padding: '10px 14px', textAlign: 'center', fontWeight: '700', color: '#475569' }}>
                      {pct}
                    </td>
                    <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                      <span style={{
                        padding: '3px 8px',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: '800',
                        background: grade === 'A+' || grade === 'A' ? '#f0fdf4' : grade === 'F' ? '#fef2f2' : '#eff6ff',
                        color: grade === 'A+' || grade === 'A' ? '#16a34a' : grade === 'F' ? '#dc2626' : '#2563eb'
                      }}>
                        {grade}
                      </span>
                    </td>
                    <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                      <select
                        value={row.attendanceStatus}
                        disabled={!isAdmin && hasExistingMarks}
                        onChange={(e) => handleRowChange(idx, 'attendanceStatus', e.target.value)}
                        style={{
                          padding: '6px 8px',
                          borderRadius: '6px',
                          border: '1px solid #cbd5e1',
                          fontSize: '11.5px',
                          fontWeight: '700',
                          background: (!isAdmin && hasExistingMarks) ? '#f1f5f9' : '#ffffff',
                          color: isAbsent ? '#dc2626' : '#16a34a',
                          cursor: (!isAdmin && hasExistingMarks) ? 'not-allowed' : 'auto'
                        }}
                      >
                        <option value="PRESENT">Present</option>
                        <option value="ABSENT">Absent</option>
                        <option value="EXEMPTED">Exempted</option>
                      </select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Save Button & Security Indicator */}
      {studentRows.length > 0 && selectedSubjectName && (
        <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
          {isAdmin ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 14px', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0', color: '#166534', fontSize: '12.5px', fontWeight: '700' }}>
              <CheckCircle2 size={16} style={{ color: '#16a34a' }} />
              <span>Administrator Authorized: You have full access to enter, edit, and update student marks.</span>
            </div>
          ) : hasExistingMarks ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 14px', background: '#fef2f2', borderRadius: '8px', border: '1px solid #fecaca', color: '#991b1b', fontSize: '12.5px', fontWeight: '600' }}>
              <Lock size={15} style={{ color: '#dc2626' }} />
              <span>Marks Protected: Marks have already been submitted. Only the School Administrator can edit marks.</span>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 14px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', color: '#475569', fontSize: '12.5px', fontWeight: '600' }}>
              <CheckCircle2 size={15} style={{ color: '#0284c7' }} />
              <span>Teacher Mode: Enter marks for your assigned class.</span>
            </div>
          )}

          {(isAdmin || !hasExistingMarks) && (
            <button
              type="button"
              onClick={handleSaveMarks}
              disabled={savingMarks}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '11px 26px',
                borderRadius: '8px',
                border: 'none',
                background: '#1e3a8a',
                color: '#ffffff',
                fontSize: '14px',
                fontWeight: '800',
                cursor: savingMarks ? 'not-allowed' : 'pointer',
                boxShadow: '0 2px 5px rgba(30,58,138,0.3)'
              }}
            >
              <Save size={16} />
              {savingMarks ? 'Saving Marks...' : (!activeTest ? 'Schedule & Save Marks' : (hasExistingMarks && isAdmin ? 'Update Marks' : 'Save Marks'))}
            </button>
          )}
        </div>
      )}

      {/* Pop-up Message Modal (Friendly dialog, not raw error msg) */}
      {popupModal.isOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            backgroundColor: 'rgba(15, 23, 42, 0.65)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
          onClick={() => setPopupModal(prev => ({ ...prev, isOpen: false }))}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#ffffff',
              borderRadius: '16px',
              maxWidth: '480px',
              width: '100%',
              padding: '28px',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.15), 0 10px 10px -5px rgba(0, 0, 0, 0.08)',
              border: '1px solid #e2e8f0',
              textAlign: 'center',
              position: 'relative'
            }}
          >
            {/* Close Button */}
            <button
              type="button"
              onClick={() => setPopupModal(prev => ({ ...prev, isOpen: false }))}
              style={{
                position: 'absolute',
                top: '14px',
                right: '14px',
                background: 'transparent',
                border: 'none',
                color: '#94a3b8',
                cursor: 'pointer',
                padding: '4px',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <X size={18} />
            </button>

            {/* Icon Circle */}
            <div
              style={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                margin: '0 auto 16px auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor:
                  popupModal.type === 'SUCCESS' ? '#ecfdf5' :
                  popupModal.type === 'LOCKED' ? '#eff6ff' : '#fffbeb',
                color:
                  popupModal.type === 'SUCCESS' ? '#059669' :
                  popupModal.type === 'LOCKED' ? '#2563eb' : '#d97706'
              }}
            >
              {popupModal.type === 'SUCCESS' ? (
                <CheckCircle2 size={34} />
              ) : popupModal.type === 'LOCKED' ? (
                <Lock size={30} />
              ) : (
                <AlertCircle size={32} />
              )}
            </div>

            {/* Title */}
            <h3
              style={{
                margin: '0 0 8px 0',
                fontSize: '19px',
                fontWeight: '800',
                color: '#0f172a',
                letterSpacing: '-0.3px'
              }}
            >
              {popupModal.title}
            </h3>

            {/* Message Body */}
            <p
              style={{
                margin: '0 0 24px 0',
                fontSize: '14px',
                color: '#475569',
                lineHeight: '1.55',
                fontWeight: '500'
              }}
            >
              {popupModal.message}
            </p>

            {/* Action button */}
            <button
              type="button"
              onClick={() => setPopupModal(prev => ({ ...prev, isOpen: false }))}
              style={{
                width: '100%',
                padding: '11px 20px',
                borderRadius: '9px',
                border: 'none',
                background:
                  popupModal.type === 'SUCCESS' ? '#059669' :
                  popupModal.type === 'LOCKED' ? '#1e3a8a' : '#d97706',
                color: '#ffffff',
                fontSize: '14px',
                fontWeight: '700',
                cursor: 'pointer',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
              }}
            >
              {popupModal.type === 'SUCCESS' ? 'Done' : 'Got It'}
            </button>
          </div>
        </div>
      )}

      {/* Quick Edit Max Marks Modal */}
      {quickEditOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(15, 23, 42, 0.6)',
          backdropFilter: 'blur(3px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '20px'
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: '16px',
            width: '100%',
            maxWidth: '540px',
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.2)',
            border: '1px solid #cbd5e1',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Header */}
            <div style={{
              background: '#1e3a8a',
              color: '#ffffff',
              padding: '16px 20px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '800' }}>
                  Edit Maximum Marks Scheme
                </h3>
                <div style={{ fontSize: '12px', color: '#93c5fd', marginTop: '2px' }}>
                  {selectedExamTitle} • {selectedSubjectName ? `${selectedSubjectName} (${selectedClass})` : selectedClass}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setQuickEditOpen(false)}
                style={{ background: 'transparent', border: 'none', color: '#ffffff', cursor: 'pointer', padding: '4px' }}
              >
                <X size={18} />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSaveQuickEditMarks} style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#334155', marginBottom: '6px' }}>
                    Base Maximum Marks
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="1000"
                    value={quickBaseMarks}
                    onChange={(e) => setQuickBaseMarks(e.target.value)}
                    required
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      borderRadius: '8px',
                      border: '1.5px solid #cbd5e1',
                      fontSize: '13.5px',
                      fontWeight: '700',
                      color: '#0f172a',
                      boxSizing: 'border-box'
                    }}
                    placeholder="e.g. 20, 40, 80"
                  />
                  <span style={{ fontSize: '11px', color: '#64748b', marginTop: '2px', display: 'block' }}>
                    Written / Theory marks
                  </span>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#334155', marginBottom: '6px' }}>
                    Additional Component
                  </label>
                  <select
                    value={quickCompType}
                    onChange={(e) => {
                      const val = e.target.value;
                      setQuickCompType(val);
                      if (val === 'NONE') {
                        setQuickCompMax(0);
                      } else if (val === 'ORAL' && (!quickCompMax || parseInt(quickCompMax, 10) === 0)) {
                        setQuickCompMax(10);
                      } else if (val === 'INTERNAL' && (!quickCompMax || parseInt(quickCompMax, 10) === 0)) {
                        setQuickCompMax(20);
                      }
                    }}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      borderRadius: '8px',
                      border: '1.5px solid #cbd5e1',
                      fontSize: '13px',
                      fontWeight: '700',
                      color: '#0f172a',
                      background: '#ffffff',
                      boxSizing: 'border-box'
                    }}
                  >
                    <option value="NONE">None (Single Marks)</option>
                    <option value="ORAL">Oral</option>
                    <option value="INTERNAL">Internal (IA)</option>
                  </select>
                  <span style={{ fontSize: '11px', color: '#64748b', marginTop: '2px', display: 'block' }}>
                    Added on top of base
                  </span>
                </div>
              </div>

              {quickCompType !== 'NONE' && (
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#334155', marginBottom: '6px' }}>
                    {quickCompType === 'ORAL' ? 'Oral Max Marks' : 'Internal Max Marks'}
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="500"
                    value={quickCompMax}
                    onChange={(e) => setQuickCompMax(e.target.value)}
                    required
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      borderRadius: '8px',
                      border: '1.5px solid #cbd5e1',
                      fontSize: '13px',
                      fontWeight: '700',
                      color: '#0f172a',
                      boxSizing: 'border-box'
                    }}
                    placeholder="e.g. 10 or 20"
                  />
                  <span style={{ fontSize: '11px', color: '#64748b', marginTop: '2px', display: 'block' }}>
                    Additional to base (NOT deducted)
                  </span>
                </div>
              )}

              {/* Total Calculation Banner */}
              <div style={{
                background: quickCompType === 'NONE' ? '#f1f5f9' : quickCompType === 'ORAL' ? '#f0fdf4' : '#eff6ff',
                border: `1.5px solid ${quickCompType === 'NONE' ? '#cbd5e1' : quickCompType === 'ORAL' ? '#86efac' : '#93c5fd'}`,
                borderRadius: '8px',
                padding: '10px 14px'
              }}>
                <div style={{ fontSize: '13.5px', fontWeight: '800', color: quickCompType === 'NONE' ? '#334155' : quickCompType === 'ORAL' ? '#166534' : '#1e40af' }}>
                  {quickCompType === 'ORAL' && `Written: ${quickBaseMarks || 0} + Oral: ${quickCompMax || 0} = Total: ${(parseInt(quickBaseMarks, 10) || 0) + (parseInt(quickCompMax, 10) || 0)} Marks`}
                  {quickCompType === 'INTERNAL' && `Theory: ${quickBaseMarks || 0} + Internal: ${quickCompMax || 0} = Total: ${(parseInt(quickBaseMarks, 10) || 0) + (parseInt(quickCompMax, 10) || 0)} Marks`}
                  {quickCompType === 'NONE' && `Total Marks: ${quickBaseMarks || 0} Marks (Single Paper)`}
                </div>
              </div>

              {/* Scope Checkboxes */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', background: '#f8fafc', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12.5px', fontWeight: '600', color: '#334155', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={quickApplyClassSubjects}
                    onChange={(e) => setQuickApplyClassSubjects(e.target.checked)}
                    style={{ width: '16px', height: '16px', accentColor: '#2563eb', cursor: 'pointer' }}
                  />
                  Apply to all subjects of {selectedExamTitle} in {selectedClass}
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12.5px', fontWeight: '600', color: '#1e3a8a', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={quickApplyAllClasses}
                    onChange={(e) => setQuickApplyAllClasses(e.target.checked)}
                    style={{ width: '16px', height: '16px', accentColor: '#1e3a8a', cursor: 'pointer' }}
                  />
                  Also apply to all other classes scheduled for {selectedExamTitle}
                </label>
              </div>

              {/* Buttons */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', paddingTop: '10px', borderTop: '1px solid #f1f5f9' }}>
                <button
                  type="button"
                  onClick={() => setQuickEditOpen(false)}
                  style={{
                    background: '#f1f5f9',
                    color: '#475569',
                    border: '1px solid #cbd5e1',
                    borderRadius: '8px',
                    padding: '8px 16px',
                    fontSize: '13px',
                    fontWeight: '700',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={quickSaving}
                  style={{
                    background: quickSaving ? '#94a3b8' : '#1e3a8a',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '8px 20px',
                    fontSize: '13px',
                    fontWeight: '700',
                    cursor: quickSaving ? 'wait' : 'pointer',
                    boxShadow: '0 2px 4px rgba(30,58,138,0.2)'
                  }}
                >
                  {quickSaving ? 'Saving...' : 'Save Scheme'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default MarksEntry;
